import type { AuthUser } from "../store/slices/authSlice";

export const ACCESS_TOKEN_KEY = "access_token";
export const TOKEN_KEY = "token";
export const REFRESH_TOKEN_KEY = "refresh_token";
export const REFRESH_TOKEN_EXPIRES_AT_KEY = "refresh_token_expires_at";
export const USER_KEY = "user";
export const REFRESH_TOKEN_LIFETIME_MS = 12 * 24 * 60 * 60 * 1000;
const ACCESS_TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

const decodeJwtPayload = (token: string) => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }

    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = window.atob(
      payload.padEnd(Math.ceil(payload.length / 4) * 4, "="),
    );

    return JSON.parse(decoded) as { exp?: number };
  } catch {
    return null;
  }
};

export const getStoredAccessToken = () => {
  return (
    localStorage.getItem(TOKEN_KEY) || localStorage.getItem(ACCESS_TOKEN_KEY)
  );
};

export const getStoredRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const getStoredUser = (): AuthUser | null => {
  const rawUser = localStorage.getItem(USER_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as AuthUser;
  } catch {
    return null;
  }
};

export const setStoredAccessToken = (token: string) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

export const setStoredRefreshToken = (
  refreshToken: string,
  expiresAt = Date.now() + REFRESH_TOKEN_LIFETIME_MS,
) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(REFRESH_TOKEN_EXPIRES_AT_KEY, expiresAt.toString());
};

export const ensureRefreshTokenExpiry = () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return null;
  }

  const existingExpiry = localStorage.getItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
  if (existingExpiry) {
    return Number(existingExpiry);
  }

  const fallbackExpiry = Date.now() + REFRESH_TOKEN_LIFETIME_MS;
  localStorage.setItem(REFRESH_TOKEN_EXPIRES_AT_KEY, fallbackExpiry.toString());
  return fallbackExpiry;
};

export const isAccessTokenExpired = (token?: string | null) => {
  if (!token) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return true;
  }

  return payload.exp * 1000 <= Date.now() + ACCESS_TOKEN_EXPIRY_BUFFER_MS;
};

export const isRefreshTokenExpired = () => {
  const expiresAt = ensureRefreshTokenExpiry();
  if (!expiresAt) {
    return true;
  }

  return expiresAt <= Date.now();
};

export const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
  localStorage.removeItem(USER_KEY);
};
